import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { FiMenu, FiX, FiPhone, FiArrowRight, FiChevronDown } from 'react-icons/fi';
import { navigation, companyData } from '../../data/content';
import SafeIcon from '../../common/SafeIcon';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Update auf JPG Logo
  const logoUrl = "https://canv.wasacon.com/ms-terassen-de/_assets/media/321d582a486cac263b1924afe8de13f7.jpg";

  return (
    <header className="bg-white sticky top-0 z-50 border-b border-ms-light/50">
      {/* Top Bar */}
      <div className="bg-ms-solar text-white py-2 text-xs md:text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <span className="font-bold tracking-wide uppercase opacity-90">{companyData.region}</span>
          <div className="flex items-center space-x-6">
            <a href={`tel:${companyData.phone.replace(/\s/g, '')}`} className="flex items-center hover:text-ms-blue transition font-bold">
              <SafeIcon icon={FiPhone} className="mr-2" />
              {companyData.phone}
            </a>
            <span className="opacity-30">|</span>
            <span className="font-bold">{companyData.phoneLandline}</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-24">
          <div className="flex-shrink-0 flex items-center">
            <NavLink to="/" className="flex items-center">
              <img src={logoUrl} alt="Oscar Terrassen" className="h-20 w-auto object-contain" />
            </NavLink>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) =>
              item.subItems ? (
                <div key={item.name} className="relative group">
                  <button className="text-sm font-black tracking-widest uppercase transition-all duration-300 border-b-2 py-1 px-2 text-ms-dark border-transparent group-hover:text-ms-blue group-hover:border-ms-blue/30 flex items-center gap-1 cursor-default">
                    {item.name}
                    <SafeIcon icon={FiChevronDown} className="transition-transform duration-300 group-hover:rotate-180" />
                  </button>
                  {/* Dropdown */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white border border-ms-light rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden">
                    {item.subItems.map((sub) => (
                      <NavLink
                        key={sub.name}
                        to={sub.path}
                        className={({ isActive }) =>
                          `block px-5 py-4 text-sm font-black uppercase tracking-widest transition-colors border-b border-ms-light/50 last:border-0 ${
                            isActive
                              ? 'bg-ms-blue/5 text-ms-blue'
                              : 'text-ms-dark hover:bg-ms-light hover:text-ms-blue'
                          }`
                        }
                      >
                        {sub.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-sm font-black tracking-widest uppercase transition-all duration-300 border-b-2 py-1 relative px-2 ${
                      isActive
                        ? 'text-ms-blue border-ms-blue bg-ms-blue/5 rounded-t-lg'
                        : 'text-ms-dark border-transparent hover:text-ms-blue hover:border-ms-blue/30'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              )
            )}

            <NavLink
              to="/kontakt"
              className="group relative inline-flex items-center justify-center px-8 py-3.5 font-black text-white uppercase tracking-tighter text-sm transition-all duration-300 bg-gradient-to-r from-ms-red to-[#ff4d4d] rounded-xl shadow-[0_10px_20px_rgba(225,6,0,0.3)] hover:shadow-[0_15px_30px_rgba(225,6,0,0.4)] hover:-translate-y-1 active:scale-95 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                Angebot anfragen
                <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#ff4d4d] to-ms-red opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </NavLink>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-ms-dark hover:text-ms-blue p-2 transition-colors"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-8 w-8" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-ms-light shadow-2xl">
          <div className="px-4 pt-4 pb-8 space-y-2">
            {navigation.map((item) =>
              item.subItems ? (
                <div key={item.name} className="space-y-1 py-2">
                  <div className="px-4 py-2 text-sm font-extrabold uppercase tracking-widest text-ms-dark/40 flex items-center gap-2">
                    {item.name}
                    <SafeIcon icon={FiChevronDown} />
                  </div>
                  <div className="pl-4 space-y-2 border-l-2 border-ms-light ml-4">
                    {item.subItems.map((sub) => (
                      <NavLink
                        key={sub.name}
                        to={sub.path}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) =>
                          `block px-4 py-3 rounded-2xl text-sm font-black uppercase tracking-widest transition-all duration-200 border-l-4 ${
                            isActive
                              ? 'bg-ms-blue/10 text-ms-blue border-ms-blue shadow-sm'
                              : 'text-ms-dark border-transparent hover:bg-ms-light hover:text-ms-solar'
                          }`
                        }
                      >
                        {sub.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block px-4 py-4 rounded-2xl text-base font-black uppercase tracking-widest transition-all duration-200 border-l-8 ${
                      isActive
                        ? 'bg-ms-blue/20 text-ms-blue border-ms-blue shadow-md'
                        : 'text-ms-dark border-transparent hover:bg-ms-light hover:text-ms-solar'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              )
            )}

            <div className="pt-4 px-2">
              <NavLink
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className="block w-full text-center bg-gradient-to-r from-ms-red to-[#ff4d4d] text-white px-5 py-5 rounded-2xl font-black uppercase tracking-tighter shadow-xl shadow-red-500/30"
              >
                Jetzt Termin vereinbaren
              </NavLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;